import { useCallback, useEffect, useMemo, useReducer } from 'react';
// utils
import axios from 'src/utils/axios';
import routes from "../../../utils/constants/apiRoutes";
//
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { localStorageAvailable } from 'src/utils/localStorageAvailable';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';
import { AuthContext } from './auth-context';
import { isValidToken, jwtDecode, setSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  isAuthenticated: false,
  loading: true
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const storageAvailable = localStorageAvailable();



  // just little different from old, nothing significant, user is brought from res.data in og.
  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem(STORAGE_KEY) : '';
      const refreshToken = storageAvailable ? localStorage.getItem(REFRESH_KEY) : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken, refreshToken);

        // const response = await axios.get('/account/my-account');

        //const { user } = response.data;
        /**
         * @todo remove above two lines for user verification
         * 
         */
        const user = jwtDecode(accessToken)

        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            user,
          },
        });

      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const response = await axios.post('/auth/login', { email, password }).catch((e: any) => {
      if (typeof e?.message !== "string") {
        console.log("Failed to manage exception form login api", e);
        throw "Internal Server error";
      } else {
        throw e;
      }
    });

    if (response?.data) {
      const { accessToken, user, refreshToken } = response?.data;
    
      setSession(accessToken, refreshToken);

      dispatch({
        type: Types.LOGIN,
        payload: {
          user,
        },
      });
    }

    return response;
  }, []);

  const verifyMFA = useCallback(async (id: string, mfaToken: string, isMFAEnabled: boolean, twoFactorSecret: string, rememberMe: boolean) => {
    const response = await axios.post('/auth/verify-mfa', { id, mfaToken, isMFAEnabled, twoFactorSecret, rememberMe }).catch((e) => {
      if (typeof e?.message !== "string") {
        console.log("Failed to manage exception form login api", e);
        throw "Internal Server error";
      } else {
        throw e;
      }
    });
    if (response?.data?.isValid === true) {
      localStorage.setItem("MFAVerified", "true");
      const { accessToken, user, refreshToken } = response?.data;
      setSession(accessToken, refreshToken);

      dispatch({
        type: Types.LOGIN,
        payload: {
          user,
        },
      });
    }

    return response;
  }, []);

  // REGISTER
  // REGISTER
  const register = useCallback(
    async (data: any, role: string) => {
      const response = await axios.post('/auth/signup', { data, role });
      const { accesstoken, user, refreshToken } = response.data;

      if (user && accesstoken) {
        setSession(accesstoken, refreshToken);

        dispatch({
          type: Types.REGISTER,
          payload: {
            user,
          },
        });
      }

      return response;
    },
    []
  );

  const register2 = useCallback(
    async (data: any, role: string) => {
      const response = await axios.post('/auth/signup2', { data, role });
      const { accesstoken, user, refreshToken } = response.data;

      if (user && accesstoken) {
        setSession(accesstoken, refreshToken);

        dispatch({
          type: Types.REGISTER,
          payload: {
            user,
          },
        });
      }

      return response;
    },
    []
  );

  // LOGIN WITH SALESFORCE
  const loginWithSalesforce = useCallback(async (authCode: string) => {
    const response = await axios.post(`${routes.SALESFORCE_LOGIN_ROUTE}/${authCode}`)
    const { accessToken, user, refreshToken } = response.data;

    setSession(accessToken, refreshToken);

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });

    return response;
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    const notificationToken = localStorage.getItem('notificationToken');
    localStorage.removeItem('notificationToken');
    localStorage.removeItem('MFAVerified');
    localStorage.removeItem(STORAGE_KEY);
    axios.post(`/auth/logout/?notificationToken=${notificationToken}`);
    setSession(null, null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  const getSessionId = useCallback(async () => {

    const response = await axios.get('/auth/consumer/createSessionId');
    return response;
  }, [])

  // Update My Profile
  const updateMyProfile = useCallback(async (id: string) => {
    const response = await axios.get(`/users/${id}`);
    if (response?.data) {
      const { user } = response?.data;
      dispatch({
        type: Types.LOGIN,
        payload: {
          user,
        },
      });
    }
    return response;
  }, []);

  const forgot = useCallback(
    async (email: string) => {
      const response = await axios.post('/auth/forgot', {
        email,
      });
      const { accessToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
    },
    []
  );

  const verifyForgotToken = useCallback(
    async (token: string) => {
      const response = await axios.post('/auth/verifyforgottoken', {
        token,
      });
      const { accessToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
      return response
    },
    []
  );

  const resetPassword = useCallback(
    async (email: string, oldPassword: string, newPassword: string) => {
      const response = await axios.patch('/auth/reset', {
        email,
        oldPassword,
        newPassword
      });
      const { accessToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
      return response;
    },
    []
  );

  const updateUser = useCallback(
    async (userData: any) => {
      const headers = {
        'Content-Type': 'multipart/form-data',
      }
      const response = await axios.put('/auth/update', userData, { headers });
      return response;
    },
    []
  );

  const forgotResetPassword = useCallback(
    async (email: string, newPassword: string) => {
      const response = await axios.post('/auth/resetforgot', {
        email,
        newPassword
      });
      const { accessToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
      return response;
    },
    []
  );

  const loginWithOtp = useCallback(async (phonenumber: string) => {
    const response = await axios.post('/auth/login-with-otp', {
      phonenumber
    });

    return response;
  }, []);

  const verifyMobileOtp = useCallback(async (otp: string, phonenumber: string) => {
    const response = await axios.post('/auth/verify-mobile-otp', {
      otp: Object.values(otp).join(""), phonenumber
    });
    return response;
  }, []);


  const verifyOtp = useCallback(async (otp: string, phonenumber: string) => {
    const response = await axios.post('/auth/verify-otp', {
      otp, phonenumber
    });

    const { accessToken, user, refreshToken } = response.data;

    setSession(accessToken, refreshToken);

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });

    return response;
  }, [])

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;
  const [document_title, setDocumentTitle] = useDocumentTitle("");

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      register,
      updateMyProfile,
      logout,
      loginWithSalesforce,
      loginWithGoogle: () => { },
      loginWithGithub: () => { },
      loginWithTwitter: () => { },
      forgot,
      verifyMobileOtp,
      getSessionId,
      loginWithOtp,
      verifyOtp,
      verifyForgotToken,
      resetPassword,
      updateUser,
      forgotResetPassword,
      setDocumentTitle,
      verifyMFA,
      register2
    }),
    [login, logout, register, state.user, status, login, logout,verifyMFA, register2]
  );

  return <AuthContext.Provider value={{ ...memoizedValue, document_title }}>{children}</AuthContext.Provider>;
}